import React, {
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import classnames from 'classnames';
import ReactSelect, { components } from 'react-select';

import { ReactComponent as ChevronDownSVG } from '../../../../assets/icons/chevron-down.svg';
import { ReactComponent as MarkSVG } from '../../../../assets/icons/mark.svg';

import './AdvancedSelect.scss';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownSVG />
  </components.DropdownIndicator>
);

interface OptionProps extends ComponentPropsWithoutRef<ElementType> {
  children?: ReactNode;
  isSelected?: boolean;
}

const Option = ({ children, isSelected, ...props }: OptionProps) => {
  return (
    <components.Option isSelected={isSelected} {...props}>
      <div className="advanced-select__option-text">{children}</div>
      {isSelected && (
        <div className="advanced-select__option-selected-icon">
          <MarkSVG />
        </div>
      )}
    </components.Option>
  );
};

export interface AdvancedSelectProps
  extends ComponentPropsWithoutRef<ElementType> {
  className?: string;
  hasError?: boolean;
  value?;
  onChange?;
  options?;
  isMulti?: boolean;
}

const AdvancedSelect = ({
  className,
  hasError,
  value,
  onChange,
  options,
  isMulti,
  ...props
}: AdvancedSelectProps) => {
  const handleChange = useCallback(
    (event) => {
      const value = isMulti ? event.map((option) => option.value) : event.value;

      if (onChange) {
        onChange(value);
      }
    },
    [onChange, isMulti],
  );

  value = isMulti ? value || [] : value;

  const selectedValue = useMemo(
    () =>
      isMulti
        ? options.filter((option) => value.some((val) => option.value === val))
        : options.find((opt) => opt.value === value),
    [isMulti, options, value],
  );

  return (
    <ReactSelect
      className={classnames(
        'advanced-select',
        { select_error: hasError },
        className,
      )}
      classNamePrefix="advanced-select"
      components={{ DropdownIndicator, Option }}
      isClearable={false}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      isMulti={isMulti}
      {...props}
    />
  );
};

export default AdvancedSelect;
